// TakeLane — one horizontal lane per immutable STS take: waveform + play +
// copy-to-master + drag-select region → MasterClip + delete (cascades to
// MasterClips referencing the take via the store method).

import { useCallback, useEffect, useRef, useState } from 'react';
import type { STSTake } from '@shared/types';
import { Waveform, type WaveformHandle } from '@/components/Waveform/Waveform';
import styles from './TakeLane.module.css';

interface Props {
  /** The take backing this lane. */
  take: STSTake;
  /** 1-based label for the lane ("Take 1", "Take 2", ...). */
  label: string;
  /** How many master clips currently reference this take — used in the
   *  delete-confirm copy so the user knows what cascades away. */
  masterClipRefCount: number;
  /** Fires when the user clicks Copy→Master. Parent is responsible for
   *  creating the MasterClip and pushing it into the store. */
  onCopyToMaster: (takeId: string) => void;
  /** Fires when the user confirms delete. Parent removes the take and
   *  cascades master-clip cleanup via `removeSTSTake`. */
  onDelete: (takeId: string) => void;
  /** Fires when this lane starts playing — the parent pauses any other
   *  lane that's currently playing (only one lane plays at a time). */
  onPlayStart?: (takeId: string) => void;
  /** Parent-driven pause signal. When this prop transitions to `true`,
   *  the lane pauses its waveform. Used to enforce single-lane playback. */
  shouldPause?: boolean;
  /** Fires when the user drag-selects a sub-range on the waveform. The
   *  parent creates a MasterClip with [start, end] as in/out points. */
  onRegionSelect?: (takeId: string, start: number, end: number) => void;
}

function fmtDur(sec: number): string {
  if (!Number.isFinite(sec) || sec <= 0) return '0:00';
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec - m * 60);
  return `${m}:${String(s).padStart(2, '0')}`;
}

export function TakeLane({
  take,
  label,
  masterClipRefCount,
  onCopyToMaster,
  onDelete,
  onPlayStart,
  shouldPause,
  onRegionSelect,
}: Props) {
  const wsRef = useRef<WaveformHandle | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);

  // Parent toggles `shouldPause` to silence this lane when another one
  // starts. Running the pause as an effect keeps React's render pure.
  useEffect(() => {
    if (shouldPause && isPlaying) {
      wsRef.current?.pause();
      setIsPlaying(false);
    }
  }, [shouldPause, isPlaying]);

  const handlePlayToggle = useCallback(() => {
    const ws = wsRef.current;
    if (!ws) return;
    if (ws.isPlaying()) {
      ws.pause();
      setIsPlaying(false);
    } else {
      if (onPlayStart) onPlayStart(take.id);
      ws.play();
      setIsPlaying(true);
    }
  }, [onPlayStart, take.id]);

  const handleWsPlay = useCallback(() => {
    // Fires when the user clicks inside the waveform (WaveSurfer's built-in
    // click-to-seek starts playback). Mirror into state + broadcast to parent
    // so sibling lanes pause.
    if (onPlayStart) onPlayStart(take.id);
    setIsPlaying(true);
  }, [onPlayStart, take.id]);

  const handleDelete = useCallback(() => {
    const msg = masterClipRefCount > 0
      ? `Delete ${label}? Removing this take will also remove ${masterClipRefCount} clip${
          masterClipRefCount === 1 ? '' : 's'
        } from the master timeline.`
      : `Delete ${label}?`;
    if (!window.confirm(msg)) return;
    onDelete(take.id);
  }, [label, masterClipRefCount, onDelete, take.id]);

  const handleCopy = useCallback(() => {
    onCopyToMaster(take.id);
  }, [onCopyToMaster, take.id]);

  const handleRegion = useCallback(
    (start: number, end: number) => {
      onRegionSelect?.(take.id, start, end);
    },
    [onRegionSelect, take.id],
  );

  return (
    <div className={styles.lane}>
      <div className={styles.meta}>
        <span className={styles.label}>{label}</span>
        <span className={styles.duration}>{fmtDur(take.duration)}</span>
      </div>
      <div className={styles.wave}>
        <Waveform
          ref={wsRef}
          audio={take.audioBuffer}
          height={56}
          onPlay={handleWsPlay}
          onRegion={handleRegion}
        />
      </div>
      <div className={styles.controls}>
        <button
          type="button"
          className={styles.ctrlBtn}
          onClick={handlePlayToggle}
          title={isPlaying ? 'Pause' : 'Play'}
          aria-label={isPlaying ? 'Pause take' : 'Play take'}
        >
          <i className={isPlaying ? 'ph-fill ph-pause' : 'ph-fill ph-play'} />
        </button>
        <button
          type="button"
          className={styles.ctrlBtn}
          onClick={handleCopy}
          title="Copy to master timeline"
          aria-label="Copy take to master timeline"
        >
          <i className="ph-bold ph-arrow-up" />
        </button>
        <button
          type="button"
          className={`${styles.ctrlBtn} ${styles.ctrlBtnDanger}`}
          onClick={handleDelete}
          title="Delete take"
          aria-label="Delete take"
        >
          <i className="ph-bold ph-trash" />
        </button>
      </div>
    </div>
  );
}
